import { useAutoAnimate } from "@formkit/auto-animate/react";
import React, { useState } from "react";

import { faPencil } from "@fortawesome/pro-solid-svg-icons";

import { Text } from "@performyard/py-components";

import Icon from "../../../components/Icon/Icon";

import { MeasureCanvas } from "../../../utils/textMeasurement/MeasureCanvas";
import { buildFontTemplate } from "../../../utils/textMeasurement/textMeasurement";

import { NAME_MAX_LENGTH } from "./todoForm";

/**
 * Pencil-icon inline rename for the workflow template name, following the form builder's
 * `EditFormTitleInput`: clicking the title or pencil swaps in an underlined input; blur
 * or submit commits. A blanked name reverts to the previous one rather than erroring
 * (the server rejects blank names, so an empty title is never allowed to stand).
 */
const TemplateTitleInput = ({ value, onChange }: { value: string; onChange: (name: string) => void }) => {
	const [isEditing, setIsEditing] = useState(false);
	const [draft, setDraft] = useState(value);
	const [invalidNameMessage, setInvalidNameMessage] = useState("");
	const [ref] = useAutoAnimate();

	const fontString = buildFontTemplate("sub-heading", "semi-bold");
	const editWidth = new MeasureCanvas().getTextWidth(draft, fontString);

	const startEditing = (): void => {
		setDraft(value);
		setInvalidNameMessage("");
		setIsEditing(true);
	};

	const commit = (): void => {
		const trimmed = draft.trim();
		if (trimmed === "") {
			setInvalidNameMessage("");
			setIsEditing(false);
			return;
		}
		if (trimmed.length > NAME_MAX_LENGTH) {
			setInvalidNameMessage(`Name has maximum length of ${NAME_MAX_LENGTH} characters`);
			return;
		}
		setInvalidNameMessage("");
		onChange(trimmed);
		setIsEditing(false);
	};

	return (
		<div>
			{isEditing ? (
				<div
					ref={ref}
					onKeyDown={(e) => {
						if (e.key === "Enter") {
							e.preventDefault();
							e.stopPropagation();
							commit();
						}
					}}
					onBlur={commit}
				>
					<div className="group flex w-full items-center gap-[8px] text-neutral-9">
						<input
							autoFocus
							className="w-max-full border-0 border-b-[0.5px] border-neutral-9 p-0 text-sub-heading font-semi-bold text-neutral-9 outline-hidden hover:cursor-text focus:border-primary-4 active:border-primary-4"
							style={{
								width: editWidth,
								minWidth: "200px",
								maxWidth: "800px",
							}}
							value={draft}
							onChange={(e) => setDraft(e.target.value)}
							aria-label="Workflow template name"
							data-testid="template-title-input"
						/>
						<button
							className="border-0 bg-transparent p-0"
							type="button"
							onClick={commit}
							aria-label="Save workflow template name"
						>
							<Icon
								className="text-neutral-5 hover:cursor-pointer hover:text-neutral-7"
								size="small"
								icon={faPencil}
							/>
						</button>
					</div>
					{invalidNameMessage && <div className="text-caption text-red-6">{invalidNameMessage}</div>}
				</div>
			) : (
				<h2 className="group m-0 flex items-center gap-[8px] text-neutral-9" onClick={startEditing}>
					<span className="min-h-[32px] max-w-[780px] overflow-hidden border-solid border-neutral-9 text-sub-heading font-semi-bold whitespace-nowrap text-neutral-9 group-hover:border-b-[0.5px] hover:cursor-text">
						<Text
							wrap={false}
							data-testid="template-name"
							weight="semi-bold"
							size="sub-heading"
							dangerouslySetClassName="py-text-truncate"
						>
							{value}
						</Text>
					</span>
					<button
						className="border-0 bg-transparent p-0"
						type="button"
						onClick={startEditing}
						aria-label="Edit workflow template name"
					>
						<Icon
							className="text-neutral-5 hover:cursor-pointer hover:text-neutral-7"
							size="small"
							icon={faPencil}
						/>
					</button>
				</h2>
			)}
		</div>
	);
};

export default TemplateTitleInput;
